import React, { useEffect, useState, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { IoClose, IoSaveOutline } from 'react-icons/io5';
import {
  IoShieldCheckmark,
  IoMail,
  IoSearchOutline,
  IoDownloadOutline,
  IoArrowUp,
  IoArrowDown,
  IoSettingsOutline,
  IoWalletOutline,
  IoPeopleOutline,
  IoListOutline,
  IoKeyOutline,
} from 'react-icons/io5';
import { VscChromeClose } from 'react-icons/vsc';

import {
  fetchAllUsers,
  exportUsersToExcel,
  updateUserAccessLevel,
  fetchAccessLevels,
  toggleUserSuspension,
  sendPasswordResetLink,
  fetchLlmSettings,
} from '../../services/api';
import { CostBreakdownModal } from './CostBreakdownModal';
import McpOriginsManager from '../../components/McpConnections/McpManagement/McpOriginsManager';
import PreApprovedUserModal from './PreApprovedUserModal';
import Pagination from './Pagination';
import { RolesPermissionsPanel } from '../../components/admin/RolesPermissionsPanel';
import { UserRolesModal } from '../../components/admin/UserRolesModal';
import { UserAvatar } from '../../components/UserAvatar';
import ActivityLogsPanel from './ActivityLogsPanel';
import LlmSettingsPanel, { countLlmProblems } from './LlmSettingsPanel';
import ConfigurationPanel from './ConfigurationPanel';
import { useAccess } from '../../services/access';
import {
  ENABLE_ADMIN_USERS_SECTION,
  ENABLE_ADMIN_COST_ACTION,
  ENABLE_ADMIN_ROLES_SECTION,
  ENABLE_MCP,
  ENABLE_ADMIN_ACTIVITY_LOGS,
} from '../../config/features';

// --- HELPER HOOK & FUNCTIONS (Unchanged) ---
// NEW: UI colors remain on the frontend, as they are a view concern.
const ACCESS_LEVEL_COLORS = {
  basic: 'bg-gray-500/10 text-gray-400',
  free: 'bg-green-500/10 text-green-400',
  paid: 'bg-[#5652CF]/10 text-blue-400',
  premium: 'bg-purple-500/10 text-purple-400',
  default: 'bg-gray-500/10 text-gray-400',
};

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(dateString));
};

export const AccessLevelModal = ({
  isOpen,
  onClose,
  user,
  onSave,
  accessLevels,
}) => {
  const [selectedLevel, setSelectedLevel] = useState(
    user?.access_level || 'basic',
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setSelectedLevel(user.access_level || 'basic');
    }
  }, [user]);

  // Render nothing if the modal is not open or config hasn't loaded
  if (!isOpen || !user || !accessLevels) return null;

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(user.id || user._id || user.user_id, selectedLevel);
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="animate-fade-in-up m-4 w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-700/50 p-4">
          <h3 className="text-lg font-bold text-zinc-200">Edit Access Level</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 transition-colors hover:bg-zinc-700"
          >
            <IoClose className="h-5 w-5 text-zinc-400" />
          </button>
        </div>
        <div className="space-y-4 p-6">
          <p className="text-sm text-zinc-400">
            Change access for{' '}
            <strong className="text-zinc-200">{user.name}</strong> ({user.email}
            ).
          </p>
          <div>
            <label
              htmlFor="accessLevel"
              className="mb-2 block text-sm font-medium text-zinc-200"
            >
              Access Level
            </label>
            <select
              id="accessLevel"
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="w-full rounded-lg border border-zinc-700/50 bg-zinc-700 px-3 py-2 text-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
            >
              {/* FIXED: Iterate over the `accessLevels` prop passed from AdminPage */}
              {Object.entries(accessLevels).map(([key, config]) => (
                <option
                  key={key}
                  value={key}
                  className="bg-zinc-900 capitalize text-zinc-200"
                >
                  {config.label}
                </option>
              ))}
            </select>
            {/* FIXED: Display description from the fetched config */}
            <p className="mt-2 h-4 text-xs text-zinc-400">
              {accessLevels[selectedLevel]?.description}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-3 rounded-b-xl bg-zinc-700 p-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-zinc-700/50 bg-transparent px-4 py-2 text-sm font-medium text-zinc-400 transition-colors hover:bg-zinc-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
          >
            <IoSaveOutline />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ResetPasswordModal = ({ isOpen, onClose, user, onConfirm }) => {
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen || !user) return null;

  const handleConfirm = async () => {
    setIsSaving(true);
    await onConfirm(user);
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="animate-fade-in-up m-4 w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-700/50 p-4">
          <h3 className="text-lg font-bold text-zinc-200">Reset Password</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 transition-colors hover:bg-zinc-700"
          >
            <IoClose className="h-5 w-5 text-zinc-400" />
          </button>
        </div>
        <div className="space-y-4 p-6">
          <p className="text-sm text-zinc-400">
            Are you sure you want to send a password reset link to{' '}
            <strong className="text-zinc-200">{user.name}</strong> ({user.email}
            )?
          </p>
          <p className="text-xs text-blue-400">
            This will send them an email with a secure link valid for 24 hours.
          </p>
        </div>
        <div className="flex items-center justify-end space-x-3 rounded-b-xl bg-zinc-700 p-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-zinc-700/50 bg-transparent px-4 py-2 text-sm font-medium text-zinc-400 transition-colors hover:bg-zinc-900"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSaving}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
          >
            {isSaving ? 'Sending...' : 'Send Link'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const SuspendConfirmationModal = ({
  isOpen,
  onClose,
  user,
  onConfirm,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen || !user) return null;

  const handleConfirm = async () => {
    setIsSaving(true);
    await onConfirm(user);
    setIsSaving(false);
  };

  const actionName = user.is_suspended ? 'Reactivate' : 'Suspend';
  const actionColor = user.is_suspended
    ? 'bg-indigo-600 hover:bg-indigo-700'
    : 'bg-red-600 hover:bg-red-700';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="animate-fade-in-up m-4 w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-700/50 p-4">
          <h3 className="text-lg font-bold text-zinc-200">{actionName} User</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 transition-colors hover:bg-zinc-700"
          >
            <IoClose className="h-5 w-5 text-zinc-400" />
          </button>
        </div>
        <div className="space-y-4 p-6">
          <p className="text-sm text-zinc-400">
            Are you sure you want to {actionName.toLowerCase()}{' '}
            <strong className="text-zinc-200">{user.name}</strong> ({user.email}
            )?
          </p>
          {!user.is_suspended && (
            <p className="text-xs text-red-400">
              They will be instantly logged out and will not be able to log back
              in until you reactivate them.
            </p>
          )}
        </div>
        <div className="flex items-center justify-end space-x-3 rounded-b-xl bg-zinc-700 p-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-zinc-700/50 bg-transparent px-4 py-2 text-sm font-medium text-zinc-400 transition-colors hover:bg-zinc-900"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSaving}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-wait disabled:opacity-50 ${actionColor}`}
          >
            {isSaving ? 'Processing...' : `Confirm ${actionName}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * AdminInfo Component
 * Displays the current admin's information.
 */
const AdminInfo = ({ userInfo }) => {
  if (!userInfo) return null;
  return (
    <div className="mb-6 rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl sm:p-5">
      <h2 className="mb-4 text-xl font-bold text-zinc-100">
        Admin Information
      </h2>
      <div className="flex items-center gap-4">
        <UserAvatar
          user={userInfo}
          className="h-14 w-14 rounded-full border-2 border-zinc-700 object-cover sm:h-16 sm:w-16"
        />
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
            {userInfo.name} <IoShieldCheckmark className="text-green-400" />
          </h3>
          <p className="flex items-center gap-2 text-sm text-zinc-400">
            <IoMail /> {userInfo.email}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Responsive User Card for Mobile View
 * Displays user data in a card format for small screens.
 */
const UserCard = ({
  user,
  onOpenUsageModal,
  onOpenAccessModal,
  onOpenRolesModal,
  accessLevelUiConfig,
  onToggleSuspend,
}) => (
  <div className="flex flex-col gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4 shadow-md">
    <div className="flex items-center gap-3">
      <UserAvatar user={user} className="h-10 w-10 rounded-full" />
      <div className="flex flex-1 items-center justify-between">
        <div>
          <p className="font-semibold text-zinc-200">
            {user.name}
            {user.is_suspended && (
              <span className="ml-2 rounded-full border border-red-500/30 bg-red-500/20 px-2 py-0.5 text-xs font-bold text-red-400">
                Suspended
              </span>
            )}
          </p>
          <p className="text-xs text-zinc-500">{user.email}</p>
        </div>
      </div>
    </div>
    <div className="space-y-2 border-t border-zinc-700/50 pt-3 text-xs text-zinc-400">
      <p className="flex items-center justify-between">
        <span>Created:</span>
        <span className="font-medium text-zinc-200">
          {formatDate(user.created_at)}
        </span>
      </p>
      <p className="flex items-center justify-between">
        <span>Last Login:</span>
        <span className="font-medium text-zinc-200">
          {formatDate(user.last_login_at)}
        </span>
      </p>
      <p className="flex items-center justify-between">
        <span>Auth Method:</span>
        <span className="font-medium capitalize text-zinc-200">
          {user.authentication_method}
        </span>
      </p>
      <p className="flex items-center justify-between">
        <span>Enterprise:</span>
        {user.enterprise_access ? (
          <span className="rounded-full bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-400">
            Yes
          </span>
        ) : (
          <span className="rounded-full bg-red-500/10 px-2.5 py-0.5 text-xs font-medium text-red-400">
            No
          </span>
        )}
      </p>
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={() => onToggleSuspend(user)}
          className="font-medium transition-opacity hover:opacity-80"
          title={user.is_suspended ? 'Reactivate User' : 'Suspend User'}
        >
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
              user.is_suspended
                ? 'bg-zinc-700/80 text-zinc-300'
                : 'bg-red-500/10 text-red-400'
            }`}
          >
            {user.is_suspended ? 'Reactivate' : 'Suspend'}
          </span>
        </button>
      </div>
      {/* NEW: Display Access Level in Card */}
      {/* FIXED: Display Access Level using the config prop */}
      <p className="flex items-center justify-between">
        <span>Access:</span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${accessLevelUiConfig[user.access_level]?.color || 'bg-gray-500/10 text-gray-400'}`}
        >
          {accessLevelUiConfig[user.access_level]?.label || 'Basic'}
        </span>
      </p>
    </div>
    {/* --- ACTION BUTTONS --- */}
    <div className="mt-2 flex flex-col gap-2 border-t border-zinc-700/50 pt-3">
      {ENABLE_ADMIN_COST_ACTION && (
        <button
          onClick={() => onOpenUsageModal(user)}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#5652CF]/10 px-4 py-2 text-center text-sm font-semibold text-blue-400 transition-colors hover:bg-[#5652CF]/20"
        >
          <IoWalletOutline />
          View Costs
        </button>
      )}
      <div className="flex gap-2">
        {ENABLE_ADMIN_ROLES_SECTION && (
          <button
            onClick={() => onOpenRolesModal(user)}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-indigo-500/10 px-4 py-2 text-center text-sm font-semibold text-indigo-400 transition-colors hover:bg-indigo-500/20"
          >
            <IoShieldCheckmark />
            Roles
          </button>
        )}
        <button
          onClick={() => onOpenAccessModal(user)}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-purple-500/10 px-4 py-2 text-center text-sm font-semibold text-purple-400 transition-colors hover:bg-purple-500/20"
        >
          <IoSettingsOutline />
          Access
        </button>
      </div>
    </div>
  </div>
);

/**
 * FilterControls Component
 * Houses all the new UI controls for search, filter, and export.
 */
const FilterControls = ({
  searchTerm,
  setSearchTerm,
  enterpriseOnly,
  setEnterpriseOnly,
  onExport,
  isExporting,
}) => (
  <div className="mb-6 flex flex-col items-center gap-4 rounded-xl border border-zinc-800 bg-zinc-900 p-4 shadow-lg md:flex-row">
    <div className="relative w-full md:flex-1">
      <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
      <input
        type="text"
        placeholder="Search by name or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full rounded-lg border border-zinc-700 bg-zinc-800 py-2 pl-10 pr-4 text-zinc-200 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
    <div className="flex w-full items-center justify-between gap-4 md:w-auto md:justify-start">
      <label className="flex cursor-pointer select-none items-center">
        <input
          type="checkbox"
          checked={enterpriseOnly}
          onChange={(e) => setEnterpriseOnly(e.target.checked)}
          className="sr-only"
        />
        <div
          className={`flex h-6 w-11 items-center rounded-full p-1 duration-300 ${enterpriseOnly ? 'bg-green-500' : 'bg-zinc-700'}`}
        >
          <div
            className={`h-4 w-4 transform rounded-full bg-white shadow-md duration-300 ${enterpriseOnly ? 'translate-x-5' : ''}`}
          ></div>
        </div>
        <span className="ml-3 text-sm font-medium text-zinc-300">
          Enterprise Only
        </span>
      </label>
      <button
        onClick={onExport}
        disabled={isExporting}
        className="flex items-center gap-2 rounded-lg bg-green-500/20 px-4 py-2 text-green-400 transition-colors hover:bg-green-500/30 disabled:cursor-wait disabled:opacity-50"
      >
        <IoDownloadOutline />
        {isExporting ? 'Exporting...' : 'Export'}
      </button>
    </div>
  </div>
);

/**
 * UsersList Component - UPDATED with Multi-Sort UI
 * Renders a responsive table with advanced sortable headers.
 */
const UsersList = ({
  users,
  loading,
  sortConfig,
  onSort,
  onOpenUsageModal,
  onOpenAccessModal,
  onOpenRolesModal,
  accessLevelUiConfig,
  onToggleSuspend,
  onOpenResetPasswordModal,
}) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="animate-pulse rounded-lg bg-zinc-900 p-4 shadow-md"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-zinc-700"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 rounded bg-zinc-700"></div>
                <div className="h-3 w-1/2 rounded bg-zinc-700"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!users?.length)
    return (
      <div className="rounded-lg bg-zinc-900 p-8 text-center">
        No users found.
      </div>
    );

  // NEW: Function to get sort icon and order badge
  const getSortInfo = (key) => {
    const sortIndex = sortConfig.findIndex((s) => s.key === key);
    if (sortIndex === -1) {
      return null; // Not being sorted
    }
    const sortItem = sortConfig[sortIndex];
    return {
      direction: sortItem.direction,
      order: sortIndex + 1,
    };
  };

  const headers = [
    { key: 'name', label: 'User' },
    { key: 'created_at', label: 'Created At' },
    { key: 'last_login_at', label: 'Last Login' },
    { key: 'authentication_method', label: 'Auth Method' },
    { key: 'enterprise_access', label: 'Enterprise' },
    { key: 'actions', label: 'Actions', sortable: false },
    { key: 'access_level', label: 'Access Level', sortable: false },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl">
      {/* Mobile View */}
      <div className="space-y-4 p-4 md:hidden">
        {users.map((user) => (
          <UserCard
            key={user.email}
            user={user}
            onOpenUsageModal={onOpenUsageModal}
            accessLevelUiConfig={accessLevelUiConfig}
            onOpenAccessModal={onOpenAccessModal}
            onOpenRolesModal={onOpenRolesModal}
            onToggleSuspend={onToggleSuspend}
            onOpenResetPasswordModal={onOpenResetPasswordModal}
          />
        ))}
      </div>

      {/* Desktop View with Multi-Sort UI */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-left text-sm text-zinc-400">
          <thead className="bg-zinc-700 text-xs uppercase text-zinc-200">
            <tr>
              {headers.map((header) => {
                const sortInfo = getSortInfo(header.key);
                return (
                  <th key={header.key} scope="col" className="px-6 py-3">
                    {/* eslint-disable-next-line */}
                    <div
                      className="flex cursor-pointer select-none items-center gap-2"
                      onClick={() => {
                        if (header.sortable !== false) {
                          onSort(header.key);
                        }
                      }}
                    >
                      {header.label}
                      {sortInfo ? (
                        <span className="flex items-center gap-1 rounded-md bg-[#5652CF] px-2 py-0.5 text-white">
                          <span>{sortInfo.order}</span>
                          {sortInfo.direction === 'asc' ? (
                            <IoArrowUp />
                          ) : (
                            <IoArrowDown />
                          )}
                        </span>
                      ) : (
                        header.sortable !== false && (
                          <VscChromeClose className="text-gray-400 opacity-20" />
                        )
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr
                key={user.email}
                className="border-b border-zinc-800 bg-zinc-900 transition-colors duration-200 hover:bg-zinc-700"
              >
                <td className="whitespace-nowrap px-6 py-4 font-medium text-zinc-200">
                  <div className="flex items-center gap-3">
                    <UserAvatar user={user} className="h-9 w-9" />
                    <div>
                      <div className="flex items-center gap-2">
                        {user.name}
                        {user.is_suspended && (
                          <span className="rounded-full border border-red-500/30 bg-red-500/20 px-2 py-0.5 text-xs font-bold text-red-400">
                            Suspended
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">{formatDate(user.created_at)}</td>
                <td className="px-6 py-4">{formatDate(user.last_login_at)}</td>
                <td className="px-6 py-4 capitalize">
                  {user.authentication_method}
                </td>
                <td className="px-6 py-4">
                  {user.enterprise_access ? (
                    <span className="me-2 rounded-full bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-400">
                      Yes
                    </span>
                  ) : (
                    <span className="me-2 rounded-full bg-red-500/10 px-2.5 py-0.5 text-xs font-medium text-red-400">
                      No
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {ENABLE_ADMIN_COST_ACTION && (
                      <button
                        onClick={() => onOpenUsageModal(user)}
                        className="font-medium transition-opacity hover:opacity-80"
                        title="View Costs"
                      >
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#5652CF]/10 px-2 py-1 text-xs font-medium text-blue-400">
                          <IoWalletOutline className="text-xs" /> Costs
                        </span>
                      </button>
                    )}
                    {ENABLE_ADMIN_ROLES_SECTION && (
                      <button
                        onClick={() => onOpenRolesModal(user)}
                        className="font-medium transition-opacity hover:opacity-80"
                        title="Manage Roles"
                      >
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-2 py-1 text-xs font-medium text-indigo-400">
                          <IoShieldCheckmark className="text-xs" /> Roles
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => onToggleSuspend(user)}
                      className="font-medium transition-opacity hover:opacity-80"
                      title={
                        user.is_suspended ? 'Reactivate User' : 'Suspend User'
                      }
                    >
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                          user.is_suspended
                            ? 'bg-zinc-700/80 text-zinc-300'
                            : 'bg-red-500/10 text-red-400'
                        }`}
                      >
                        {user.is_suspended ? 'Reactivate' : 'Suspend'}
                      </span>
                    </button>
                    <button
                      onClick={() => onOpenResetPasswordModal(user)}
                      className="font-medium transition-opacity hover:opacity-80"
                      title="Reset Password"
                    >
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-1 text-xs font-medium text-blue-400">
                        <IoKeyOutline className="text-xs" /> Reset
                      </span>
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onOpenAccessModal(user)}
                      className="font-medium transition-opacity hover:opacity-80"
                      title="Edit Access Level"
                    >
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${accessLevelUiConfig[user.access_level]?.color || 'bg-gray-500/10 text-gray-400'}`}
                      >
                        {accessLevelUiConfig[user.access_level]?.label ||
                          'Basic'}
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Main AdminPage Component
 * Manages all state and logic, now with advanced multi-field sorting.
 */

const AdminPage = ({ userIdToken, userInfo }) => {
  // Each tab needs its own permission. The Configuration operator holds only
  // admin:config:* (no admin:access), so it sees the Configuration tab alone.
  const access = useAccess();
  const canSeeLlm = access.can('admin:llm:view');
  const canSeeConfig = access.can('admin:config:view');
  const showUsers =
    ENABLE_ADMIN_USERS_SECTION && access.can('admin:users:view');
  const showRoles = ENABLE_ADMIN_ROLES_SECTION && access.can('admin:access');
  const showMcp = ENABLE_MCP && access.can('admin:mcp:view');
  const showActivityLogs =
    ENABLE_ADMIN_ACTIVITY_LOGS && access.can('admin:access');
  const [usersData, setUsersData] = useState({
    users: [],
    total_pages: 0,
    total_users: 0,
  });
  const [loading, setLoading] = useState(showUsers);
  const [isExporting, setIsExporting] = useState(false);

  // --- STATE MANAGEMENT ---
  const [userRefreshKey, setUserRefreshKey] = useState(0); // Key to force re-fetching user data
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [enterpriseOnly, setEnterpriseOnly] = useState(false);

  // NEW: Sort state is now an array to handle multiple fields
  const [sortConfig, setSortConfig] = useState([
    { key: 'created_at', direction: 'desc' },
    { key: 'last_login_at', direction: 'desc' },
  ]);
  // --- New states for modals and costs ---
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
  const [selectedUserForUsage, setSelectedUserForUsage] = useState(null);

  // +++ NEW: STATE FOR BACKEND-DRIVEN CONFIG +++
  const [accessLevelsConfig, setAccessLevelsConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(showUsers);

  // +++ NEW: STATE FOR MODALS +++
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState(null);

  // +++ NEW: State for the pre-approved user modal +++
  const [isPreApprovedModalOpen, setIsPreApprovedModalOpen] = useState(false);

  // +++ NEW: State for Suspend Modal +++
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [selectedUserForSuspend, setSelectedUserForSuspend] = useState(null);

  // +++ NEW: State for Reset Password Modal +++
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);
  const [selectedUserForResetPassword, setSelectedUserForResetPassword] =
    useState(null);

  // +++ NEW: State for Roles Modal +++
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
  const [selectedUserForRoles, setSelectedUserForRoles] = useState(null);

  // AI providers and models tab: Super Admin only; "!" counts unset items.
  const [llmProblems, setLlmProblems] = useState(0);
  const onLlmSettings = useCallback(
    (settings) => setLlmProblems(countLlmProblems(settings)),
    [],
  );
  useEffect(() => {
    if (!canSeeLlm || !userIdToken) return;
    fetchLlmSettings(userIdToken)
      .then(onLlmSettings)
      .catch(() => {});
  }, [canSeeLlm, userIdToken, onLlmSettings]);

  // +++ NEW: State for Tab Navigation +++
  const [activeTab, setActiveTab] = useState(
    showUsers
      ? 'users'
      : showRoles
        ? 'roles'
        : showMcp
          ? 'mcp'
          : showActivityLogs
            ? 'activity-logs'
            : canSeeConfig
              ? 'configuration'
              : '',
  );
  // NEW: Dynamically generate the UI config from the fetched data
  const accessLevelUiConfig = useMemo(() => {
    if (!accessLevelsConfig) return {};
    return Object.keys(accessLevelsConfig).reduce((acc, key) => {
      acc[key] = {
        label: accessLevelsConfig[key].label || key,
        color: ACCESS_LEVEL_COLORS[key] || ACCESS_LEVEL_COLORS.default,
      };
      return acc;
    }, {});
  }, [accessLevelsConfig]);

  // NEW: Fetch configuration on initial load
  useEffect(() => {
    const loadAdminConfig = async () => {
      if (userIdToken) {
        try {
          setIsConfigLoading(true);
          const config = await fetchAccessLevels(userIdToken);
          setAccessLevelsConfig(config);
        } catch (error) {
          toast.error('Failed to load admin configuration.');
        } finally {
          setIsConfigLoading(false);
        }
      }
    };
    if (showUsers) {
      loadAdminConfig();
    } else {
      setIsConfigLoading(false);
    }
  }, [userIdToken, showUsers]);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const PAGE_LIMIT = 10;

  // --- DATA FETCHING ---
  const loadAllUsers = useCallback(
    async (page, search, sort, enterprise) => {
      if (!userIdToken || !showUsers) return;
      setLoading(true);
      try {
        const params = {
          page,
          limit: PAGE_LIMIT,
          search,
          sortConfig: sort,
          enterpriseOnly: enterprise,
        };
        const data = await fetchAllUsers(userIdToken, params);
        setUsersData(data);
      } catch (error) {
        toast.error(
          `Failed to fetch users: ${error.message || 'Unknown error'}`,
        );
        setUsersData({ users: [], total_pages: 0, total_users: 0 });
      } finally {
        setLoading(false);
      }
    },
    [userIdToken, showUsers],
  );

  useEffect(() => {
    if (userIdToken && showUsers) {
      loadAllUsers(
        currentPage,
        debouncedSearchTerm,
        sortConfig,
        enterpriseOnly,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRefreshKey]);

  // Effect for handling filters, search, and sorting changes
  useEffect(() => {
    // Reset to page 1 whenever filters change
    setCurrentPage(1);
    loadAllUsers(1, debouncedSearchTerm, sortConfig, enterpriseOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, sortConfig, enterpriseOnly, loadAllUsers]);

  // Effect for handling page changes separately
  useEffect(() => {
    loadAllUsers(currentPage, debouncedSearchTerm, sortConfig, enterpriseOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // --- HANDLERS ---
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= usersData.total_pages) {
      setCurrentPage(newPage);
    }
  };

  // NEW: Advanced sort handler with 3 states (desc -> asc -> off)
  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newConfig = [...prevConfig];
      const sortIndex = newConfig.findIndex((s) => s.key === key);

      if (sortIndex !== -1) {
        // Field exists, move to next state
        if (newConfig[sortIndex].direction === 'desc') {
          // State 1 (desc) -> State 2 (asc)
          newConfig[sortIndex].direction = 'asc';
        } else {
          // State 2 (asc) -> State 3 (off)
          newConfig.splice(sortIndex, 1);
        }
      } else {
        // Field does not exist, add it
        // State 0 (off) -> State 1 (desc)
        newConfig.push({ key, direction: 'desc' });
      }
      return newConfig;
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportUsersToExcel(userIdToken);
      toast.success('Export started! Your download will begin shortly.');
    } catch (error) {
      toast.error(`Export failed: ${error.message || 'Please try again.'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // --- New Handlers for Modals ---
  const handleOpenUsageModal = (user) => {
    setSelectedUserForUsage(user);
    setIsUsageModalOpen(true);
  };

  const handleCloseUsageModal = () => {
    setIsUsageModalOpen(false);
    setSelectedUserForUsage(null);
  };

  // +++ NEW: HANDLERS FOR ACCESS LEVEL MODAL +++
  const handleOpenAccessModal = (user) => {
    setSelectedUserForAccess(user);
    setIsAccessModalOpen(true);
  };

  const handleCloseAccessModal = () => {
    setIsAccessModalOpen(false);
    setSelectedUserForAccess(null);
  };

  const handleSaveAccessLevel = async (userId, newLevel) => {
    const toastId = toast.loading('Updating access level...');
    try {
      await updateUserAccessLevel(userIdToken, userId, newLevel);

      // Optimistically update the UI without a full refresh
      setUsersData((prevData) => ({
        ...prevData,
        users: prevData.users.map((user) =>
          (user.id || user._id || user.user_id) === userId
            ? { ...user, access_level: newLevel }
            : user,
        ),
      }));

      toast.success('Access level updated!', { id: toastId });
      handleCloseAccessModal(); // Close modal on success
    } catch (error) {
      toast.error(`Failed to update: ${error.message || 'Unknown error'}`, {
        id: toastId,
      });
    } finally {
      setUserRefreshKey((prev) => prev + 1); // Trigger re-fetch
    }
  };

  const handleOpenSuspendModal = (user) => {
    setSelectedUserForSuspend(user);
    setIsSuspendModalOpen(true);
  };

  const handleCloseSuspendModal = () => {
    setIsSuspendModalOpen(false);
    setSelectedUserForSuspend(null);
  };

  const handleConfirmSuspend = async (user) => {
    const actionText = user.is_suspended ? 'reactivating' : 'suspending';
    const toastId = toast.loading(`User is ${actionText}...`);
    try {
      await toggleUserSuspension(
        userIdToken,
        user.id || user._id || user.user_id,
        !user.is_suspended,
      );

      setUsersData((prevData) => ({
        ...prevData,
        users: prevData.users.map((u) =>
          (u.id || u._id || u.user_id) === (user.id || user._id || user.user_id)
            ? { ...u, is_suspended: !user.is_suspended }
            : u,
        ),
      }));

      toast.success(
        `User successfully ${user.is_suspended ? 'reactivated' : 'suspended'}!`,
        { id: toastId },
      );
      handleCloseSuspendModal();
    } catch (error) {
      toast.error(`Failed: ${error.message || 'Unknown error'}`, {
        id: toastId,
      });
    }
  };

  const handleOpenResetPasswordModal = (user) => {
    setSelectedUserForResetPassword(user);
    setIsResetPasswordModalOpen(true);
  };

  const handleCloseResetPasswordModal = () => {
    setIsResetPasswordModalOpen(false);
    setSelectedUserForResetPassword(null);
  };

  const handleConfirmResetPassword = async (user) => {
    const toastId = toast.loading(`Sending password reset link...`);
    try {
      await sendPasswordResetLink(
        userIdToken,
        user.id || user._id || user.user_id,
      );
      toast.success(`Password reset link sent to ${user.email}`, {
        id: toastId,
      });
      handleCloseResetPasswordModal();
    } catch (error) {
      toast.error(`Failed: ${error.message || 'Unknown error'}`, {
        id: toastId,
      });
    }
  };

  // +++ NEW: HANDLERS FOR ROLES MODAL +++
  const handleOpenRolesModal = (user) => {
    setSelectedUserForRoles(user);
    setIsRolesModalOpen(true);
  };

  const handleCloseRolesModal = () => {
    setIsRolesModalOpen(false);
    setSelectedUserForRoles(null);
  };

  if (isConfigLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-950 text-zinc-200">
        Loading Configuration...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 to-zinc-900 p-4 text-zinc-200 sm:p-6 md:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl font-bold text-zinc-100">
            Admin Control Panel
          </h1>
          <p className="mt-1 text-zinc-400 sm:mt-0">
            Manage and monitor all user activity.
          </p>
        </div>

        <AdminInfo userInfo={userInfo} />

        <div className="mb-6 mt-6 border-b border-zinc-800">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {showUsers && (
              <button
                onClick={() => setActiveTab('users')}
                className={`${activeTab === 'users' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'} whitespace-nowrap border-b-2 px-1 pb-4 text-lg font-medium transition-colors`}
              >
                Users
              </button>
            )}
            {showRoles && (
              <button
                onClick={() => setActiveTab('roles')}
                className={`${activeTab === 'roles' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'} whitespace-nowrap border-b-2 px-1 pb-4 text-lg font-medium transition-colors`}
              >
                <span className="flex items-center gap-2">
                  <IoShieldCheckmark />
                  Roles & Permissions
                </span>
              </button>
            )}
            {showMcp && (
              <button
                onClick={() => setActiveTab('mcp')}
                className={`${activeTab === 'mcp' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'} whitespace-nowrap border-b-2 px-1 pb-4 text-lg font-medium transition-colors`}
              >
                MCP Connections
              </button>
            )}
            {canSeeLlm && (
              <button
                onClick={() => setActiveTab('ai-models')}
                className={`${activeTab === 'ai-models' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'} whitespace-nowrap border-b-2 px-1 pb-4 text-lg font-medium transition-colors`}
              >
                <span className="flex items-center gap-2">
                  AI Models
                  {llmProblems > 0 && (
                    <span
                      className="rounded-full bg-amber-500/20 px-2 text-sm font-semibold text-amber-400"
                      title={`${llmProblems} item(s) not set up`}
                    >
                      ! {llmProblems}
                    </span>
                  )}
                </span>
              </button>
            )}
            {canSeeConfig && (
              <button
                onClick={() => setActiveTab('configuration')}
                className={`${activeTab === 'configuration' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'} whitespace-nowrap border-b-2 px-1 pb-4 text-lg font-medium transition-colors`}
              >
                Configuration
              </button>
            )}
            {showActivityLogs && (
              <button
                onClick={() => setActiveTab('activity-logs')}
                className={`${activeTab === 'activity-logs' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'} whitespace-nowrap border-b-2 px-1 pb-4 text-lg font-medium transition-colors`}
              >
                <span className="flex items-center gap-2">
                  <IoListOutline />
                  Activity Logs
                </span>
              </button>
            )}
          </nav>
        </div>
        {showUsers && activeTab === 'users' && (
          <div className="mt-8 animate-fade-in">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-zinc-100">
                User Management
              </h2>
              <div className="flex items-center gap-2">
                {/* +++ NEW: Button to open the pre-approved user manager +++ */}
                <button
                  onClick={() => setIsPreApprovedModalOpen(true)}
                  className="flex items-center gap-2 rounded-lg border border-zinc-700/50 bg-zinc-700 px-3 py-2 text-sm transition-colors hover:bg-zinc-900"
                  title="Manage Pre-Approved Users"
                >
                  <IoPeopleOutline />
                  <span>Pre-Approved</span>
                </button>
              </div>
            </div>
            <FilterControls
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              enterpriseOnly={enterpriseOnly}
              setEnterpriseOnly={setEnterpriseOnly}
              onExport={handleExport}
              isExporting={isExporting}
            />
            <UsersList
              users={usersData.users}
              loading={loading}
              sortConfig={sortConfig}
              onSort={handleSort}
              onOpenUsageModal={handleOpenUsageModal} // Pass the handler
              onOpenAccessModal={handleOpenAccessModal} // Pass the handler down
              onOpenRolesModal={handleOpenRolesModal} // Pass the Roles handler down
              onToggleSuspend={handleOpenSuspendModal}
              onOpenResetPasswordModal={handleOpenResetPasswordModal}
              // Pass the dynamically generated config to the list
              accessLevelUiConfig={accessLevelUiConfig}
            />
            <Pagination
              currentPage={usersData.current_page || 1}
              totalPages={usersData.total_pages}
              totalUsers={usersData.total_users}
              limit={PAGE_LIMIT}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* +++ NEW ROLES & PERMISSIONS SECTION +++ */}
        {showRoles && activeTab === 'roles' && (
          <div className="animate-fade-in">
            <RolesPermissionsPanel userIdToken={userIdToken} />
          </div>
        )}

        {/* +++ NEW MCP MANAGEMENT SECTION +++ */}
        {showMcp && activeTab === 'mcp' && (
          <div className="animate-fade-in">
            <McpOriginsManager userIdToken={userIdToken} />
          </div>
        )}

        {canSeeLlm && activeTab === 'ai-models' && (
          <div className="mt-4 animate-fade-in">
            <LlmSettingsPanel
              userIdToken={userIdToken}
              onSettings={onLlmSettings}
            />
          </div>
        )}

        {canSeeConfig && activeTab === 'configuration' && (
          <div className="mt-4 animate-fade-in">
            <ConfigurationPanel userIdToken={userIdToken} />
          </div>
        )}

        {/* +++ ACTIVITY LOGS SECTION +++ */}
        {showActivityLogs && activeTab === 'activity-logs' && (
          <div className="mt-4 animate-fade-in">
            <ActivityLogsPanel userIdToken={userIdToken} />
          </div>
        )}
      </div>
      {selectedUserForUsage && (
        <CostBreakdownModal
          isOpen={isUsageModalOpen}
          onClose={handleCloseUsageModal}
          user={selectedUserForUsage}
          userIdToken={userIdToken}
        />
      )}
      {/* +++ NEW: Render the PreApprovedUserModal +++ */}
      <PreApprovedUserModal
        isOpen={isPreApprovedModalOpen}
        onClose={() => setIsPreApprovedModalOpen(false)}
        userIdToken={userIdToken}
        accessLevelsConfig={accessLevelUiConfig}
      />

      {/* +++ NEW: RENDER THE ACCESS LEVEL MODAL +++ */}
      {selectedUserForAccess && (
        <AccessLevelModal
          isOpen={isAccessModalOpen}
          onClose={handleCloseAccessModal}
          user={selectedUserForAccess}
          onSave={handleSaveAccessLevel}
          // Pass the raw fetched config to the modal
          accessLevels={accessLevelsConfig}
        />
      )}

      {/* +++ NEW: RENDER THE USER ROLES MODAL +++ */}
      {selectedUserForRoles && (
        <UserRolesModal
          isOpen={isRolesModalOpen}
          onClose={handleCloseRolesModal}
          user={selectedUserForRoles}
          userIdToken={userIdToken}
        />
      )}

      {/* +++ NEW: RENDER THE SUSPEND MODAL +++ */}
      {selectedUserForSuspend && (
        <SuspendConfirmationModal
          isOpen={isSuspendModalOpen}
          onClose={handleCloseSuspendModal}
          user={selectedUserForSuspend}
          onConfirm={handleConfirmSuspend}
        />
      )}

      {/* +++ NEW: RENDER THE RESET PASSWORD MODAL +++ */}
      {selectedUserForResetPassword && (
        <ResetPasswordModal
          isOpen={isResetPasswordModalOpen}
          onClose={handleCloseResetPasswordModal}
          user={selectedUserForResetPassword}
          onConfirm={handleConfirmResetPassword}
        />
      )}
    </div>
  );
};

export default AdminPage;
